/**
 * Programul și designul unui ecran: ce rulează acum, ce liste are asignate, ce programări are și
 * ce excepții (sărbători, zile speciale).
 *
 * Este locul unde se răspunde la întrebarea „de ce arată televizorul din sală așa?" — fără să
 * ghicească nimeni. Serverul calculează decizia exact așa cum o calculează și pentru player, deci
 * ce scrie aici este ce se vede pe ecran în secunda respectivă.
 */
import { useEffect, useMemo, useState } from 'react';
import { ROMANIA_CLOCK, ROMANIA_TZ } from '../lib/romania-time.js';
import {
  Badge,
  Button,
  Clock24,
  Field,
  Icon,
  InlineAlert,
  Input,
  Modal,
  Select,
  Skeleton,
  cx,
  useToast,
} from './ui.js';
import { api } from '../lib/api.js';
import { formatDateTime, useResource } from '../lib/hooks.js';
import { errorMessage, useSession } from '../lib/session.js';

interface Device {
  id: string;
  name: string;
  zone: string | null;
  timezone: string;
}

interface Playlist {
  id: string;
  name: string;
  template_code: string;
  item_count?: number;
}

interface Assignment {
  id: string;
  playlist_id: string;
  playlist_name: string;
  priority: number;
  is_active: boolean;
}

interface ScheduleRow {
  id: string;
  playlist_id: string;
  playlist_name: string;
  name: string;
  timezone: string;
  weekdays: number[];
  starts_at_time: string;
  ends_at_time: string;
  valid_from: string | null;
  valid_until: string | null;
  priority: number;
  is_active: boolean;
}

interface ExceptionRow {
  id: string;
  playlist_id: string | null;
  playlist_name: string | null;
  exception_date: string;
  action: 'override' | 'skip';
}

interface Preview {
  decision: { playlistId: string | null; scheduleId: string | null; reason: string; label: string | null };
  playlistName: string | null;
  itemCount: number;
  nextChangeAt: string | null;
  schedules: Array<{ id: string; name: string; window: string; label: string; active: boolean; localTime: string; playlistName: string }>;
}

const REASON_LABEL: Record<string, string> = {
  scheduled: 'Rulează o programare',
  exception_override: 'Rulează o excepție (zi specială)',
  exception_skip: 'Programările sunt oprite azi',
  assignment: 'Rulează lista asignată',
  none: 'Plan implicit (meniul publicat)',
};

/** Ordinea în care se citesc zilele în România: luni → duminică. Valorile rămân cele din baza de date. */
const WEEKDAYS: Array<{ value: number; label: string }> = [
  { value: 1, label: 'Lu' },
  { value: 2, label: 'Ma' },
  { value: 3, label: 'Mi' },
  { value: 4, label: 'Jo' },
  { value: 5, label: 'Vi' },
  { value: 6, label: 'Sâ' },
  { value: 0, label: 'Du' },
];

export function DeviceDesignModal({ device, onClose }: { device: Device | null; onClose: () => void }) {
  const session = useSession();
  const toast = useToast();
  const canEdit = session.can('display.update');

  const preview = useResource<Preview>(() => api.get<Preview>(`/displays/${device!.id}/preview`), [device?.id]);
  const assignments = useResource<Assignment[]>(() => api.get<Assignment[]>(`/displays/${device!.id}/assignments`), [device?.id]);
  const schedules = useResource<ScheduleRow[]>(() => api.get<ScheduleRow[]>(`/displays/${device!.id}/schedules`), [device?.id]);
  const exceptions = useResource<ExceptionRow[]>(() => api.get<ExceptionRow[]>(`/displays/${device!.id}/exceptions`), [device?.id]);
  const playlists = useResource<Playlist[]>(() => api.get<Playlist[]>('/playlists'), []);

  const [selection, setSelection] = useState<string[]>([]);
  const [savingAssignments, setSavingAssignments] = useState(false);

  useEffect(() => {
    setSelection((assignments.data ?? []).filter((entry) => entry.is_active).map((entry) => entry.playlist_id));
  }, [assignments.data]);

  const refreshAll = () => {
    preview.reload();
    assignments.reload();
    schedules.reload();
    exceptions.reload();
  };

  if (!device) return null;

  async function saveAssignments() {
    setSavingAssignments(true);
    try {
      await api.put(`/displays/${device!.id}/assignments`, { playlistIds: selection });
      toast.success('Listele au fost salvate', 'Ecranul preia noua listă în câteva secunde.');
      refreshAll();
    } catch (error) {
      toast.error('Nu am putut salva listele', errorMessage(error));
    } finally {
      setSavingAssignments(false);
    }
  }

  function toggleAssignment(id: string) {
    setSelection((current) => (current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id]));
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={`Program și design · ${device.name}`}
      description="Ce rulează acum pe acest ecran, ce liste are asignate și când se schimbă."
      footer={
        <Button variant="ghost" onClick={onClose}>
          Închide
        </Button>
      }
    >
      {/* derularea o face `Modal` (antetul și butonul „Închide" rămân pe loc) */}
      <div className="flex flex-col gap-6">
        {/* ── Ce rulează acum ── */}
        <section className="flex flex-col gap-3">
          <h3 className="font-display text-title text-ink-950">Ce rulează acum</h3>
          {preview.loading && !preview.data ? (
            <Skeleton className="h-24" />
          ) : preview.error ? (
            <InlineAlert tone="warning" title="Nu am putut calcula ce rulează acum">
              {preview.error}
            </InlineAlert>
          ) : preview.data ? (
            <div className="flex flex-col gap-3 rounded-[var(--radius-lg)] border border-ink-200 p-4">
              <div className="flex flex-wrap items-center gap-3">
                <Badge tone={preview.data.decision.reason === 'none' ? 'neutral' : 'success'}>
                  {REASON_LABEL[preview.data.decision.reason] ?? preview.data.decision.reason}
                </Badge>
                <span className="text-body text-ink-900">{preview.data.playlistName ?? 'Meniul publicat'}</span>
                <span className="text-body-s text-ink-600">{preview.data.itemCount} elemente</span>
              </div>
              {preview.data.nextChangeAt ? (
                <p className="flex items-center gap-2 text-body-s text-ink-600">
                  <Icon name="clock" size={14} />
                  Se schimbă automat {formatDateTime(preview.data.nextChangeAt)} (
                  {relativeShort(preview.data.nextChangeAt)})
                </p>
              ) : (
                <p className="text-body-s text-ink-500">Fără schimbare programată: rămâne pe acest conținut.</p>
              )}
              {preview.data.decision.label ? (
                <p className="text-body-s text-ink-500">Program: {preview.data.decision.label}</p>
              ) : null}

              {preview.data.schedules.length ? (
                <div className="flex flex-col divide-y divide-ink-100">
                  {preview.data.schedules.map((entry) => (
                    <div key={entry.id} className="flex flex-wrap items-center gap-3 py-2 text-body-s">
                      <span className={cx('h-2 w-2 rounded-full', entry.active ? 'bg-success' : 'bg-ink-300')} aria-hidden />
                      <span className="text-ink-900">{entry.name}</span>
                      <span className="nums text-ink-600">{entry.window}</span>
                      <span className="text-ink-500">{entry.label}</span>
                      <span className="ml-auto text-ink-400">
                        {entry.active ? `activ · ${entry.localTime} local` : `inactiv · ${entry.localTime} local`}
                      </span>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
        </section>

        {/* ── Listele asignate ── */}
        <section className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="font-display text-title text-ink-950">Liste asignate</h3>
            {canEdit ? (
              <Button variant="secondary" onClick={() => void saveAssignments()} loading={savingAssignments} disabled={!assignments.data}>
                Salvează listele
              </Button>
            ) : null}
          </div>
          <p className="text-body-s text-ink-600">
            Dacă nu există nicio programare activă, ecranul afișează prima listă bifată. Ordinea se schimbă prin
            bifare/debifare.
          </p>
          {playlists.data?.length ? (
            <div className="flex flex-col divide-y divide-ink-100 rounded-[var(--radius-md)] border border-ink-200">
              {playlists.data.map((playlist) => {
                const position = selection.indexOf(playlist.id);
                return (
                  <label key={playlist.id} className="flex cursor-pointer items-center gap-3 p-3">
                    <input
                      type="checkbox"
                      checked={position >= 0}
                      disabled={!canEdit}
                      onChange={() => toggleAssignment(playlist.id)}
                      className="h-4 w-4 accent-[var(--color-brand-600)]"
                    />
                    <span className="flex-1 text-body text-ink-900">{playlist.name}</span>
                    {playlist.item_count != null ? <span className="text-body-s text-ink-500">{playlist.item_count} elemente</span> : null}
                    {position >= 0 ? <Badge tone="info">poziția {position + 1}</Badge> : null}
                  </label>
                );
              })}
            </div>
          ) : (
            <InlineAlert tone="info" title="Nu există liste de redare">
              Creează întâi o listă în pagina „Liste de redare", apoi o poți asigna acestui ecran.
            </InlineAlert>
          )}
        </section>

        {/* ── Programări ── */}
        <SchedulesSection
          deviceId={device.id}
          canEdit={canEdit}
          schedules={schedules.data ?? []}
          loading={schedules.loading && !schedules.data}
          playlists={playlists.data ?? []}
          onChanged={refreshAll}
        />

        {/* ── Excepții ── */}
        <ExceptionsSection
          deviceId={device.id}
          canEdit={canEdit}
          exceptions={exceptions.data ?? []}
          loading={exceptions.loading && !exceptions.data}
          playlists={playlists.data ?? []}
          onChanged={refreshAll}
        />
      </div>
    </Modal>
  );
}

/* ─────────────────────────────── programările ─────────────────────────────── */

function SchedulesSection({
  deviceId,
  canEdit,
  schedules,
  loading,
  playlists,
  onChanged,
}: {
  deviceId: string;
  canEdit: boolean;
  schedules: ScheduleRow[];
  loading: boolean;
  playlists: Playlist[];
  onChanged: () => void;
}) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [playlistId, setPlaylistId] = useState('');
  const [weekdays, setWeekdays] = useState<number[]>([1, 2, 3, 4, 5, 6, 0]);
  const [startsAtTime, setStartsAtTime] = useState('18:00');
  const [endsAtTime, setEndsAtTime] = useState('23:30');
  const [validFrom, setValidFrom] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [priority, setPriority] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setName('');
      setPlaylistId(playlists[0]?.id ?? '');
      setWeekdays([1, 2, 3, 4, 5, 6, 0]);
      setStartsAtTime('18:00');
      setEndsAtTime('23:30');
      setValidFrom('');
      setValidUntil('');
      setPriority(0);
    }
  }, [open, playlists]);

  const overnight = useMemo(() => endsAtTime <= startsAtTime, [startsAtTime, endsAtTime]);

  async function create() {
    setBusy(true);
    try {
      await api.post(`/displays/${deviceId}/schedules`, {
        name,
        playlistId,
        weekdays,
        startsAtTime,
        endsAtTime,
        validFrom: validFrom || null,
        validUntil: validUntil || null,
        timezone: ROMANIA_TZ,
      });
      toast.success('Programarea a fost adăugată', 'Ecranul intră în program la ora stabilită.');
      setOpen(false);
      onChanged();
    } catch (error) {
      toast.error('Nu am putut adăuga programarea', errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function toggleActive(schedule: ScheduleRow, isActive: boolean) {
    try {
      await api.patch(`/schedules/${schedule.id}`, { isActive });
      onChanged();
    } catch (error) {
      toast.error('Nu am putut schimba programarea', errorMessage(error));
    }
  }

  async function remove(schedule: ScheduleRow) {
    try {
      await api.del(`/schedules/${schedule.id}`);
      toast.success('Programarea a fost ștearsă');
      onChanged();
    } catch (error) {
      toast.error('Nu am putut șterge programarea', errorMessage(error));
    }
  }

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-display text-title text-ink-950">Programări</h3>
        {canEdit ? (
          <Button variant="secondary" onClick={() => setOpen((value) => !value)} disabled={!playlists.length}>
            {open ? 'Renunță' : 'Adaugă o programare'}
          </Button>
        ) : null}
      </div>
      <p className="text-body-s text-ink-600">
        Fusul folosit la programări: <span className="text-ink-900">{ROMANIA_CLOCK}</span>. O fereastră care trece peste miezul
        nopții (22:00 → 02:00) este tratată ca „începe astăzi, se termină mâine".
      </p>

      {open ? (
        <div className="flex flex-col gap-4 rounded-[var(--radius-md)] border border-dashed border-ink-300 p-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Nume" required hint={'Ce înțelege echipa: „Seara", „Micul dejun", „Weekend".'}>
              {(props) => <Input {...props} value={name} onChange={(event) => setName(event.target.value)} placeholder="Seara" autoFocus />}
            </Field>
            <Field label="Listă de redare" required>
              {(props) => (
                <Select {...props} value={playlistId} onChange={(event) => setPlaylistId(event.target.value)}>
                  {playlists.map((playlist) => (
                    <option key={playlist.id} value={playlist.id}>
                      {playlist.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="De la ora" required hint="00:00–23:59">
              {(props) => <Clock24 id={props.id} value={startsAtTime} onChange={setStartsAtTime} />}
            </Field>
            <Field label="Până la ora" required hint={overnight ? '00:00–23:59. Trece peste miezul nopții: se termină a doua zi.' : '00:00–23:59'}>
              {(props) => <Clock24 id={props.id} value={endsAtTime} onChange={setEndsAtTime} />}
            </Field>
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-body-s font-medium text-ink-800">Zilele săptămânii</span>
            <div className="flex flex-wrap gap-2">
              {WEEKDAYS.map((day) => {
                const active = weekdays.includes(day.value);
                return (
                  <button
                    key={day.value}
                    type="button"
                    aria-pressed={active}
                    onClick={() =>
                      setWeekdays((current) =>
                        current.includes(day.value) ? current.filter((value) => value !== day.value) : [...current, day.value],
                      )
                    }
                    className={cx(
                      'transition-ui h-10 w-12 rounded-[var(--radius-sm)] border text-body-s',
                      active ? 'border-brand-600 bg-brand-50 text-brand-800' : 'border-ink-200 text-ink-600',
                    )}
                  >
                    {day.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Valabilă din" hint={`Opțional, ${ROMANIA_CLOCK}`}>
              {(props) => <Input {...props} type="date" value={validFrom} onChange={(event) => setValidFrom(event.target.value)} />}
            </Field>
            <Field label="Valabilă până" hint={`Opțional, ziua inclusiv, ${ROMANIA_CLOCK}`}>
              {(props) => <Input {...props} type="date" value={validUntil} onChange={(event) => setValidUntil(event.target.value)} />}
            </Field>
            <Field label="Prioritate" hint="La suprapunere câștigă valoarea mai mare.">
              {(props) => (
                <Input {...props} type="number" min={-100} max={100} value={priority} onChange={(event) => setPriority(Number(event.target.value))} />
              )}
            </Field>
          </div>

          <div className="flex justify-end">
            <Button onClick={() => void create()} loading={busy} disabled={!name.trim() || !playlistId || !weekdays.length}>
              Adaugă programarea
            </Button>
          </div>
        </div>
      ) : null}

      {loading ? (
        <Skeleton className="h-16" />
      ) : schedules.length ? (
        <div className="flex flex-col divide-y divide-ink-100 rounded-[var(--radius-md)] border border-ink-200">
          {schedules.map((schedule) => (
            <div key={schedule.id} className="flex flex-wrap items-center gap-3 p-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-body text-ink-900">
                  {schedule.name} <span className="text-ink-500">· {schedule.playlist_name}</span>
                </p>
                <p className="text-body-s text-ink-600">
                  <span className="nums">
                    {schedule.starts_at_time}–{schedule.ends_at_time}
                  </span>{' '}
                  · {describeWeekdays(schedule.weekdays)}
                  {schedule.valid_from || schedule.valid_until
                    ? ` · ${schedule.valid_from ?? '…'} → ${schedule.valid_until ?? '…'}`
                    : ''}
                  {schedule.priority ? ` · prioritate ${schedule.priority}` : ''}
                </p>
              </div>
              {schedule.is_active ? <Badge tone="success">activă</Badge> : <Badge tone="neutral">oprită</Badge>}
              {canEdit ? (
                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => void toggleActive(schedule, !schedule.is_active)}
                    aria-label={schedule.is_active ? `Oprește programarea ${schedule.name}` : `Pornește programarea ${schedule.name}`}
                  >
                    {schedule.is_active ? 'Oprește' : 'Pornește'}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => void remove(schedule)} aria-label={`Șterge programarea ${schedule.name}`}>
                    ✕
                  </Button>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-body-s text-ink-500">Nicio programare: ecranul afișează lista asignată, fără variații pe ore.</p>
      )}
    </section>
  );
}

/* ─────────────────────────────── excepțiile ─────────────────────────────── */

function ExceptionsSection({
  deviceId,
  canEdit,
  exceptions,
  loading,
  playlists,
  onChanged,
}: {
  deviceId: string;
  canEdit: boolean;
  exceptions: ExceptionRow[];
  loading: boolean;
  playlists: Playlist[];
  onChanged: () => void;
}) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState('');
  const [action, setAction] = useState<'override' | 'skip'>('override');
  const [playlistId, setPlaylistId] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setDate(new Date().toISOString().slice(0, 10));
      setAction('override');
      setPlaylistId(playlists[0]?.id ?? '');
    }
  }, [open, playlists]);

  async function create() {
    setBusy(true);
    try {
      await api.post(`/displays/${deviceId}/exceptions`, {
        date,
        action,
        playlistId: action === 'override' ? playlistId : null,
      });
      toast.success('Excepția a fost salvată', 'În ziua respectivă ecranul urmează regula specială.');
      setOpen(false);
      onChanged();
    } catch (error) {
      toast.error('Nu am putut salva excepția', errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    try {
      await api.del(`/schedules/exceptions/${id}`);
      toast.success('Excepția a fost ștearsă');
      onChanged();
    } catch (error) {
      toast.error('Nu am putut șterge excepția', errorMessage(error));
    }
  }

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-display text-title text-ink-950">Excepții (zile speciale)</h3>
        {canEdit ? (
          <Button variant="secondary" onClick={() => setOpen((value) => !value)}>
            {open ? 'Renunță' : 'Adaugă o excepție'}
          </Button>
        ) : null}
      </div>
      <p className="text-body-s text-ink-600">
        De Crăciun, de Revelion sau în ziua în care restaurantul este închis: o zi anume în care programul normal nu se
        aplică.
      </p>

      {open ? (
        <div className="flex flex-col gap-4 rounded-[var(--radius-md)] border border-dashed border-ink-300 p-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Ziua" required hint={ROMANIA_CLOCK}>
              {(props) => <Input {...props} type="date" value={date} onChange={(event) => setDate(event.target.value)} />}
            </Field>
            <Field label="Ce se întâmplă">
              {(props) => (
                <Select {...props} value={action} onChange={(event) => setAction(event.target.value as 'override' | 'skip')}>
                  <option value="override">Se afișează altă listă</option>
                  <option value="skip">Fără program în ziua aceea</option>
                </Select>
              )}
            </Field>
            {action === 'override' ? (
              <Field label="Lista de redare" required>
                {(props) => (
                  <Select {...props} value={playlistId} onChange={(event) => setPlaylistId(event.target.value)}>
                    {playlists.map((playlist) => (
                      <option key={playlist.id} value={playlist.id}>
                        {playlist.name}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            ) : (
              <div className="self-end">
                <p className="text-body-s text-ink-500">Ecranul revine la lista asignată.</p>
              </div>
            )}
          </div>
          <div className="flex justify-end">
            <Button onClick={() => void create()} loading={busy} disabled={!date || (action === 'override' && !playlistId)}>
              Salvează excepția
            </Button>
          </div>
        </div>
      ) : null}

      {loading ? (
        <Skeleton className="h-14" />
      ) : exceptions.length ? (
        <div className="flex flex-col divide-y divide-ink-100 rounded-[var(--radius-md)] border border-ink-200">
          {exceptions.map((exception) => (
            <div key={exception.id} className="flex flex-wrap items-center gap-3 p-3">
              <span className="nums text-body text-ink-900">{exception.exception_date}</span>
              <Badge tone={exception.action === 'override' ? 'info' : 'neutral'}>
                {exception.action === 'override' ? 'listă specială' : 'fără program'}
              </Badge>
              <span className="flex-1 text-body-s text-ink-600">{exception.playlist_name ?? '—'}</span>
              {canEdit ? (
                <Button variant="ghost" size="sm" onClick={() => void remove(exception.id)} aria-label={`Șterge excepția din ${exception.exception_date}`}>
                  ✕
                </Button>
              ) : null}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-body-s text-ink-500">Nicio excepție: programul obișnuit se aplică în toate zilele.</p>
      )}
    </section>
  );
}

function describeWeekdays(weekdays: number[]): string {
  const set = new Set(weekdays);
  if (set.size === 7) return 'în fiecare zi';
  const workdays = [1, 2, 3, 4, 5];
  if (workdays.every((day) => set.has(day)) && !set.has(0) && !set.has(6)) return 'luni–vineri';
  if (set.has(0) && set.has(6) && set.size === 2) return 'weekend';
  return WEEKDAYS.filter((day) => set.has(day.value))
    .map((day) => day.label)
    .join(' ');
}

function relativeShort(iso: string): string {
  const minutes = Math.round((new Date(iso).getTime() - Date.now()) / 60000);
  if (minutes <= 0) return 'acum';
  if (minutes < 60) return `în ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `în ${hours} h`;
  return `în ${Math.round(hours / 24)} zile`;
}
